import { PROP_DEFS, getProp, getItem } from '../data/items.js';
import { Repo } from '../data/schema.js';
import { EV } from '../core/eventbus.js';
import { drawProp } from '../render/props_art.js';

/**
 * 布置（编辑）模式：
 *  - 点住屋里任意器物拖到别处放下，位置写回存档
 *  - 从右侧挑一件新器物，点画面空地放下（消耗材料）
 *  - 选中后可「收起」，退回一半材料
 * 手机上用手指点、拖；电脑上用鼠标点、拖，滚轮/双指缩放看全局。
 *
 * 联机：同一频道的人共用一个屋子，摆下一件、挪一件、收起一件都发到网上（消息 t='pr'），
 * 别人那边照着改自己的场景与存档；进屋或开布置时问一圈（ask），屋里的人回一份整屋摆设（all）。
 */

/** 人是不是正站在这块地上：家具压上去人就走不动了 */
export function playerInTheWay(scene, gx, gy, w, h) {
  const p = scene && scene.first ? scene.first('transform', 'player') : null;
  if (!p) return false;
  const ts = scene.tilemap ? scene.tilemap.tileSize : 40;
  const l = gx * ts, r = (gx + w) * ts;
  const t = (gy + h * 0.45) * ts, b = (gy + h) * ts;   // 与行走碰撞一致：家具只挡下半身
  const m = 10;                                        // 脚部碰撞盒再留点余量
  return p.transform.x > l - m && p.transform.x < r + m && p.transform.y > t && p.transform.y < b + 4;
}

/** 能不能放在这儿：不出界、不压墙、不与同层器物重叠、不把人埋进去 */
export function canPlace(scene, type, gx, gy, ignoreId) {
  const tm = scene.tilemap;
  if (!tm) return false;
  const def = getProp(type);
  const w = def.w || 1;
  const h = def.h || 1;
  if (gx < 0 || gy < 0 || gx + w > tm.w || gy + h > tm.h) return false;

  for (let y = gy; y < gy + h; y++) {
    for (let x = gx; x < gx + w; x++) {
      if (tm.isSolid(x, y)) return false;          // 墙
    }
  }
  const layer = def.layer || 'furniture';
  for (const p of scene.props) {
    if (p.id === ignoreId) continue;
    const pl = getProp(p.type).layer || 'furniture';
    if (layer === 'floor' || pl === 'floor') {
      if (layer !== pl) continue;                  // 地毯可以铺在家具下面
    }
    if (gx < p.gx + p.w && p.gx < gx + w && gy < p.gy + p.h && p.gy < gy + h) return false;
  }
  if (def.solid && playerInTheWay(scene, gx, gy, w, h)) return false;
  return true;
}

export class EditMode {
  constructor(engine, { toast } = {}) {
    this.engine = engine;
    this.toast = toast || (() => {});
    this.net = null;          // 联机网（main.js 注入 NetProxy）；没连就各摆各的
    this._pending = [];       // 别的图的改动先攒着，走到那张图再补
    this._answerTimer = null;
    this._lastTip = 0;
    this.active = false;
    this.selected = null;     // 选中的器物
    this.placing = null;      // 正要放置的新器物类型
    this.ghost = null;        // 预览：{ type, gx, gy, w, h, ok, id? }
    this.dragFrom = null;     // 抓取时的格内偏移
    this.moved = false;

    this.panel = document.getElementById('edit');
    this.tipEl = document.getElementById('edit-tip');
    this.listEl = document.getElementById('edit-list');
    this.removeBtn = document.getElementById('edit-remove');
    this.doneBtn = document.getElementById('edit-done');
    this._prevGrid = engine.renderer ? engine.renderer.showGrid : false;

    this._bindPanel();
    this._bindCanvas();
    // 换图：把攒下的别的图的改动补上（也可能是别人趁我不在挪了东西）
    if (engine.events) engine.events.on(EV.SCENE_ENTER, () => this._flushPending());
  }

  /* ---------------------------------- 开关 --------------------------------- */

  toggle(force) {
    const on = force === undefined ? !this.active : !!force;
    if (on === this.active) return;
    this.active = on;
    const r = this.engine.renderer;
    if (on) {
      this._prevGrid = r.showGrid;
      r.showGrid = true;
      r.overlay = (ctx) => this._overlay(ctx);
      this.panel && this.panel.classList.remove('hidden');
      this._buildList();
      this.toast('布置：拖器物挪位置，或挑一件新的往屋里放', 'info');
      this._share('ask');           // 问问屋里现在的摆设，别照着旧样子动手
    } else {
      r.showGrid = this._prevGrid;
      r.overlay = null;
      this.selected = null;
      this.placing = null;
      this.ghost = null;
      this.dragFrom = null;
      this.panel && this.panel.classList.add('hidden');
    }
    this._syncPanel();
  }

  /* ---------------------------------- 事件 --------------------------------- */

  _bindPanel() {
    if (this.doneBtn) {
      this.doneBtn.onclick = () => this.toggle(false);
      this.doneBtn.textContent = '退出布置';
    }
    if (this.removeBtn) {
      this.removeBtn.onclick = () => this._remove();
    }
    const close = this.panel && this.panel.querySelector('[data-close]');
    if (close) close.onclick = () => this.toggle(false);
  }

  _bindCanvas() {
    const canvas = this.engine.renderer.canvas;
    canvas.addEventListener('pointerdown', (e) => this._down(e));
    canvas.addEventListener('pointermove', (e) => this._move(e));
    canvas.addEventListener('pointerup', (e) => this._up(e));
    canvas.addEventListener('pointercancel', () => { this.dragFrom = null; });
    canvas.addEventListener('pointerleave', () => { if (!this.placing) this.ghost = null; });
  }

  _toWorld(e) {
    const canvas = this.engine.renderer.canvas;
    const r = canvas.getBoundingClientRect();
    const sx = (e.clientX - r.left) * canvas.width / r.width;
    const sy = (e.clientY - r.top) * canvas.height / r.height;
    return this.engine.camera.screenToWorld(sx, sy);
  }

  _cell(e) {
    const ts = this.engine.scene.tilemap.tileSize;
    const w = this._toWorld(e);
    return { gx: Math.floor(w.x / ts), gy: Math.floor(w.y / ts) };
  }

  _hit(gx, gy) {
    const props = this.engine.scene.props;
    for (let i = props.length - 1; i >= 0; i--) {
      const p = props[i];
      if (gx >= p.gx && gx < p.gx + p.w && gy >= p.gy && gy < p.gy + p.h) return p;
    }
    return null;
  }

  _down(e) {
    if (!this.active || !e.isPrimary) return;
    const { gx, gy } = this._cell(e);

    if (this.placing) { this._place(gx, gy); return; }

    const hit = this._hit(gx, gy);
    if (hit) {
      this.selected = hit;
      this.dragFrom = { dx: gx - hit.gx, dy: gy - hit.gy };
      this.moved = false;
      this.ghost = { type: hit.type, gx: hit.gx, gy: hit.gy, w: hit.w, h: hit.h, ok: true, id: hit.id };
      e.preventDefault();
    } else {
      this.selected = null;
      this.ghost = null;
    }
    this._syncPanel();
  }

  _move(e) {
    if (!this.active || !e.isPrimary) return;
    const { gx, gy } = this._cell(e);
    const scene = this.engine.scene;

    if (this.placing) {
      const def = getProp(this.placing);
      this.ghost = {
        type: this.placing, gx, gy,
        w: def.w || 1, h: def.h || 1,
        ok: canPlace(scene, this.placing, gx, gy),
      };
      return;
    }

    if (this.dragFrom && this.selected) {
      const nx = gx - this.dragFrom.dx;
      const ny = gy - this.dragFrom.dy;
      if (nx !== this.selected.gx || ny !== this.selected.gy) {
        this.moved = true;
        this.ghost = {
          type: this.selected.type, gx: nx, gy: ny,
          w: this.selected.w, h: this.selected.h,
          ok: canPlace(scene, this.selected.type, nx, ny, this.selected.id),
          id: this.selected.id,
        };
      }
    }
  }

  _up() {
    if (!this.active) return;
    const scene = this.engine.scene;
    if (this.dragFrom && this.moved && this.ghost && this.selected) {
      const { gx, gy } = this.ghost;
      if (canPlace(scene, this.selected.type, gx, gy, this.selected.id)) {
        this.selected.gx = gx;
        this.selected.gy = gy;
        this.engine.db.table('props').update(this.selected.id, { gx, gy });
        this._persist();
        this._share('move', { id: this.selected.id, gx, gy });
        this.toast(`挪好了：${getProp(this.selected.type).name}`, 'good');
      } else {
        const def = getProp(this.selected.type);
        const w = this.selected.w, h = this.selected.h;
        this.toast(def.solid && playerInTheWay(scene, gx, gy, w, h)
          ? '你自己还站在那儿，先让开一步' : '那儿放不下，换个地方', 'warn');
      }
    }
    this.dragFrom = null;
    if (!this.placing) this.ghost = null;
    this._syncPanel();
  }

  /* -------------------------------- 放置 / 收起 ------------------------------ */

  _place(gx, gy) {
    const type = this.placing;
    const def = getProp(type);
    const scene = this.engine.scene;
    const db = this.engine.db;

    if (!canPlace(scene, type, gx, gy)) {
      const w = def.w || 1, h = def.h || 1;
      this.toast(def.solid && playerInTheWay(scene, gx, gy, w, h)
        ? '你自己还站在那儿，先让开一步' : '这儿放不下，换个地方', 'warn');
      return;
    }

    const cost = def.cost || {};
    for (const [k, v] of Object.entries(cost)) {
      if (Repo.countItem(db, k) < v) {
        this.toast(`材料不够：${getItem(k).name}×${v}`, 'warn');
        return;
      }
    }
    for (const [k, v] of Object.entries(cost)) Repo.takeItem(db, k, v);

    // 器物号自己起：带时间与人，各端各号不会撞（撞号会让别人那件顶掉自己这件）
    const pid = `pr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const row = db.table('props').insert({ id: pid, scene: scene.id, type, gx, gy });
    const prop = {
      id: (row && row.id != null) ? row.id : pid,
      type, gx, gy,
      w: def.w || 1, h: def.h || 1,
      solid: !!def.solid,
      state: { ...(def.state || {}) },
      def,
    };
    scene.props.push(prop);
    this.selected = prop;
    this.ghost = { type, gx, gy, w: prop.w, h: prop.h, ok: true, id: prop.id };

    this.engine.events.emit(EV.INVENTORY_ADD, { id: null, count: 0 });
    this._persist();
    this._share('put', { id: prop.id, type, gx, gy });
    this.toast(`放下一${def.name}`, 'good');
    this._syncPanel();
  }

  _remove() {
    if (!this.selected) { this.toast('先点一件要收起的器物', 'warn'); return; }
    const def = getProp(this.selected.type);
    const db = this.engine.db;
    for (const [k, v] of Object.entries(def.cost || {})) {
      const back = Math.floor(v / 2);
      if (back > 0) Repo.addItem(db, k, back);
    }
    const goneId = this.selected.id;
    db.table('props').remove(goneId);
    this._share('del', { id: goneId });
    this.engine.scene.props = this.engine.scene.props.filter((p) => p !== this.selected);
    this.toast(`收起了${def.name}，退回一半材料`, 'info');
    this.selected = null;
    this.ghost = null;
    this.engine.events.emit(EV.INVENTORY_ADD, { id: null, count: 0 });
    this._persist();
    this._syncPanel();
  }

  /** 改动落盘，免得刚摆好就关页面 */
  _persist() {
    const db = this.engine.db;
    if (db && typeof db.save === 'function') db.save();
  }

  /* --------------------------------- 联机同步 -------------------------------- */

  /** 我现在在哪张图 */
  _sceneId() { return (this.engine.scene && this.engine.scene.id) || ''; }

  /** 动一处就说一处，同屋的人立刻看得见 */
  _share(op, data = {}) {
    const net = this.net;
    if (!net || !net.open) return;
    net.send('pr', { op, scene: this._sceneId(), ...data });
  }

  /** 整屋摆设的清单：只带位置，锅里缸里各是各的东西，不跟着发 */
  _snapshot() {
    const scene = this.engine.scene;
    if (!scene || !scene.props) return [];
    return scene.props.map((p) => ({ id: p.id, type: p.type, gx: p.gx, gy: p.gy }));
  }

  /** 有人来问（或刚进屋）：把整屋摆设给他一份。几个人同时答应就错开一下，别都挤着发 */
  _answer() {
    const net = this.net;
    if (!net || !net.open || this._answerTimer) return;
    if (!this._snapshot().length) return;
    this._answerTimer = setTimeout(() => {
      this._answerTimer = null;
      this._share('all', { items: this._snapshot() });
    }, 200 + Math.random() * 600);
  }

  /** 网上来的：动一件改一件，有人问就甩一份整屋的过去 */
  onNet(msg) {
    const net = this.net;
    if (!msg || !net || !net.open) return;
    if (msg.from === net.selfId) return;                              // 自己发的
    if (msg.t === 'hello') { this._answer(); return; }
    if (msg.t !== 'pr') return;
    if (msg.op === 'ask') { this._answer(); return; }
    const here = this._sceneId();
    if ((msg.scene || here) !== here) { this._pending.push(msg); return; }   // 别的图，先攒着
    this._apply(msg);
  }

  /** 走到那张图了：把攒下的改动补上 */
  _flushPending() {
    if (!this._pending.length) return;
    const here = this._sceneId();
    const rest = [];
    for (const msg of this._pending) {
      if ((msg.scene || here) === here) this._apply(msg); else rest.push(msg);
    }
    this._pending = rest;
  }

  _apply(msg) {
    if (this._answerTimer) { clearTimeout(this._answerTimer); this._answerTimer = null; }
    const scene = this.engine.scene;
    if (!scene || !this.engine.db) return;
    let changed = false;
    if (msg.op === 'put' && msg.type) changed = this._put(msg);
    else if (msg.op === 'move') changed = this._moveRemote(msg);
    else if (msg.op === 'del') changed = this._del(msg);
    else if (msg.op === 'all' && Array.isArray(msg.items)) changed = this._absorb(msg.items);
    if (!changed) return;
    this._persist();
    if (this.active) this._syncPanel();
    this._hint();
  }

  _put({ id, type, gx, gy }) {
    const scene = this.engine.scene;
    if (id == null || !type) return false;
    if (scene.props.some((p) => String(p.id) === String(id))) return false;
    const def = getProp(type);
    if (!def || !def.name) return false;
    const w = def.w || 1, h = def.h || 1;
    const state = { ...(def.state || {}) };
    this.engine.db.table('props').insert({ id, scene: scene.id, type, gx, gy, w, h, state });
    scene.props.push({ id, type, gx, gy, w, h, solid: !!def.solid, state, def });
    return true;
  }

  /** 名字别叫 _move：那个是鼠标拖动的处理，盖掉就没法拖家具了 */
  _moveRemote({ id, gx, gy }) {
    const p = this.engine.scene.props.find((x) => String(x.id) === String(id));
    if (!p || (p.gx === gx && p.gy === gy)) return false;
    p.gx = gx; p.gy = gy;
    this.engine.db.table('props').update(p.id, { gx, gy });
    return true;
  }

  _del({ id }) {
    const scene = this.engine.scene;
    const i = scene.props.findIndex((x) => String(x.id) === String(id));
    if (i < 0) return false;
    const [p] = scene.props.splice(i, 1);
    this.engine.db.table('props').remove(p.id);
    if (this.selected === p) { this.selected = null; this.ghost = null; }
    return true;
  }

  /** 整屋对一遍：清单里没有的收走，位置不对的挪过去，缺的补上 */
  _absorb(items) {
    const scene = this.engine.scene;
    if (!items.length) return false;
    const want = new Map(items.filter((it) => it && it.id != null).map((it) => [String(it.id), it]));
    let changed = false;
    for (const p of [...scene.props]) {
      const it = want.get(String(p.id));
      if (!it) { if (this._del({ id: p.id })) changed = true; continue; }
      if (p.gx !== it.gx || p.gy !== it.gy) {
        if (this._moveRemote({ id: p.id, gx: it.gx, gy: it.gy })) changed = true;
      }
    }
    for (const it of want.values()) {
      if (!scene.props.some((p) => String(p.id) === String(it.id))) { if (this._put(it)) changed = true; }
    }
    return changed;
  }

  _hint() {
    if (this.active) return;                   // 自己正摆着，看得见
    const now = Date.now();
    if (now - this._lastTip < 3000) return;
    this._lastTip = now;
    this.toast('屋里有人动了摆设', 'info');
  }

  /* ---------------------------------- 面板 --------------------------------- */

  _buildList() {
    if (!this.listEl) return;
    const list = Object.entries(PROP_DEFS).filter(([, d]) => d.cost);
    this.listEl.innerHTML = '';
    for (const [id, d] of list) {
      const cost = Object.entries(d.cost || {})
        .map(([k, v]) => `${getItem(k).name}×${v}`).join(' ');
      const card = document.createElement('button');
      card._type = id;
      card.className = 'edit-card' + (this.placing === id ? ' on' : '');
      card.innerHTML = `<b>${d.name}</b><i>${cost || '免费'}</i>`;
      card.onclick = () => {
        this.placing = this.placing === id ? null : id;
        this.selected = null;
        this.ghost = null;
        this._syncPanel();
        this._buildList();
      };
      this.listEl.appendChild(card);
    }
  }

  _syncPanel() {
    if (this.tipEl) {
      if (this.placing) this.tipEl.textContent = `点画面放下「${getProp(this.placing).name}」，再点一次卡片取消`;
      else if (this.selected) this.tipEl.textContent = `选中：${getProp(this.selected.type).name}，拖动或点别处挪动`;
      else this.tipEl.textContent = '点住器物拖到别处放下，或挑一件新的';
    }
    if (this.removeBtn) {
      this.removeBtn.disabled = !this.selected;
      this.removeBtn.textContent = this.selected ? `收起${getProp(this.selected.type).name}` : '收起选中';
    }
    if (this.listEl) {
      for (const card of this.listEl.children) {
        card.classList.toggle('on', card.querySelector('b') && this.placing === card._type);
      }
    }
  }

  /* --------------------------------- 覆盖层 -------------------------------- */

  _overlay(ctx) {
    const ts = this.engine.scene.tilemap.tileSize;

    // 选中的器物：金框
    if (this.selected) {
      const p = this.selected;
      ctx.save();
      ctx.strokeStyle = '#c8a45c';
      ctx.lineWidth = 2;
      ctx.setLineDash([7, 4]);
      ctx.strokeRect(p.gx * ts + 2, p.gy * ts + 2, p.w * ts - 4, p.h * ts - 4);
      ctx.restore();
    }

    const g = this.ghost;
    if (!g) return;
    const def = getProp(g.type);
    const x = g.gx * ts, y = g.gy * ts, w = g.w * ts, h = g.h * ts;

    ctx.save();
    ctx.globalAlpha = 0.45;
    drawProp(ctx, { type: g.type, gx: g.gx, gy: g.gy, w: g.w, h: g.h, state: { ...(def.state || {}) } }, { tileSize: ts, time: 0 });
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = g.ok ? 'rgba(75,143,106,0.16)' : 'rgba(200,86,75,0.2)';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = g.ok ? '#4b8f6a' : '#c8564b';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
    ctx.restore();
  }
}
